import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play, CheckCircle2, Sparkles, Globe, Terminal, Building2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getAuthUrl } from "@/utils/redirectUtils";

interface VideoData {
  id: string;
  youtubeId: string;
  tabLabel: string;
  icon: typeof Play;
  badge: string;
  title: string;
  description: string;
  highlights: string[];
}

const videos: VideoData[] = [
  {
    id: "e2e",
    youtubeId: "TRHXACAwa38",
    tabLabel: "🇬🇧 UK Legal Campaign",
    icon: Play,
    badge: "5-Minute End-to-End Walkthrough",
    title: "Watch a Campaign Built & Published in Under 5 Minutes",
    description:
      "Watch how Upfixe Ads scrapes a URL, discovers bid-filtered keywords from Google Keyword Planner, clusters 5 psychological buyer intent groups, writes full RSA copy, and publishes directly to Google Ads.",
    highlights: [
      "URL scraped with Cheerio & Puppeteer",
      "5 thematic buyer psychology ad groups",
      "15 headlines & 4 descriptions per group",
      "Negative keywords automatically attached",
      "Published safely paused to Google Ads"
    ]
  },
  {
    id: "international",
    youtubeId: "G5pY2KFHcC4",
    tabLabel: "🇩🇪 German International",
    icon: Globe,
    badge: "Multilingual & International",
    title: "Launch International Campaigns in Any Language & Country",
    description:
      "Watch Upfixe Ads configure an international campaign for Personio (personio.de) targeting Germany in native German, setting local EUR (€) currency, discovering 144 keywords, and publishing live.",
    highlights: [
      "Target language & country localized",
      "144 keywords discovered & CPA bid filtered",
      "Zero English copy leakage across ad copy",
      "Native German headlines & descriptions",
      "Verified live in Google Ads dashboard"
    ]
  },
  {
    id: "mcp",
    youtubeId: "e1tZ_8QwdTA",
    tabLabel: "⚡ AI Chat & MCP Demo",
    icon: Terminal,
    badge: "Model Context Protocol (MCP)",
    title: "Control Google Ads from Any AI Terminal (MCP Demo)",
    description:
      "Watch how Upfixe Ads connects directly to an AI chat terminal (Claude Desktop, Perplexity, Cursor) via MCP to check account status, seed keywords, refine draft copy conversationally, and safely publish paused to Google Ads without opening a browser.",
    highlights: [
      "27 built-in Google Ads MCP tools",
      "Query multi-brand status in seconds",
      "Conversational negative keyword editing",
      "Explicit user approval before publishing",
      "Full MCP audit logging in dashboard"
    ]
  },
  {
    id: "brands",
    youtubeId: "V0gbmefdwOc",
    tabLabel: "🏢 Brand Setup & Value Prop",
    icon: Building2,
    badge: "Brand Grounding & AI Context",
    title: "Import Google Ads Accounts & Generate Brand Value Prop",
    description:
      "Learn how to import Google Ads manager (MCC) and client accounts, scrape your homepage, and generate a 9-section Brand Value Proposition document to ground all AI ad campaigns in your unique value.",
    highlights: [
      "MCC manager & client account import",
      "Automated homepage deep scraper",
      "9-section value proposition document",
      "Customizable pricing & target demographics",
      "Grounds all future RSA copy generation"
    ]
  }
];

const VideoShowcaseSection = () => {
  const [activeVideoId, setActiveVideoId] = useState<string>("e2e");
  const activeVideo = videos.find((v) => v.id === activeVideoId) || videos[0];

  return (
    <section className="py-20 md:py-28 bg-gradient-to-b from-background via-muted/30 to-background border-t border-border/60 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-10">
          <Badge variant="outline" className="border-primary/40 text-primary bg-primary/10 text-xs mb-3">
            <Sparkles className="h-3 w-3 mr-1.5 inline" />
            Live Product Walkthroughs
          </Badge>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
            See Upfixe Ads in Action
          </h2>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Watch real campaigns scraped, clustered into 5 buyer intent groups, and published directly to Google Ads — via our web dashboard or your favorite AI chat terminal.
          </p>

          {/* Interactive Tab Switcher */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2 p-1.5 rounded-2xl bg-card border border-border shadow-sm max-w-3xl mx-auto">
            {videos.map((video) => {
              const Icon = video.icon;
              const isActive = activeVideo.id === video.id;
              return (
                <button
                  key={video.id}
                  type="button"
                  onClick={() => setActiveVideoId(video.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                    isActive
                      ? "bg-primary text-primary-foreground shadow-md shadow-primary/20"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/80"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{video.tabLabel}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Video Player & Details Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeVideo.id}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.3 }}
            className="max-w-5xl mx-auto bg-card rounded-2xl border border-border shadow-xl overflow-hidden"
          >
            {/* Player Container */}
            <div className="relative w-full aspect-video bg-black overflow-hidden">
              <iframe
                key={activeVideo.id}
                className="w-full h-full"
                src={`https://www.youtube-nocookie.com/embed/${activeVideo.youtubeId}?rel=0`}
                title={activeVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>

            {/* Video Context Footer */}
            <div className="p-6 md:p-8 bg-card flex flex-col md:flex-row md:items-center justify-between gap-6 border-t border-border">
              <div className="flex-1 space-y-3">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs font-semibold text-primary bg-primary/10">
                    {activeVideo.badge}
                  </Badge>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-foreground">
                  {activeVideo.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
                  {activeVideo.description}
                </p>

                {/* Bullet Highlights */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                  {activeVideo.highlights.map((point, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                      <span>{point}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row md:flex-col gap-3 shrink-0">
                <Button
                  size="lg"
                  onClick={() => (window.location.href = getAuthUrl())}
                  className="w-full sm:w-auto font-semibold px-6 shadow-md shadow-primary/20"
                >
                  Start 7-Day Free Trial
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <p className="text-[11px] text-muted-foreground text-center">
                  Zero risk · All campaigns land paused
                </p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default VideoShowcaseSection;
